/**
 * Pratt (precedence-climbing) parser for the formula grammar in the design
 * document. No parser generator, no `eval`, no regular-expression backtracking
 * on the whole input: just the tokenizer plus one precedence table.
 *
 * Precedence, loosest to tightest:
 *   `=` `<>` `<` `<=` `>` `>=`  <  `&`  <  `+` `-`  <  `*` `/`  <  `^` (right)
 *   <  prefix `+`/`-`  <  postfix `%`  <  primary
 *
 * The leading `=` of a formula is optional: `parseFormula('=A1+1')` and
 * `parseFormula('A1+1')` produce the same tree. A single `=` immediately at the
 * start is consumed as the formula introducer; anywhere else `=` is the
 * comparison operator.
 *
 * Failures return `ParseErr` with a character `offset` that is always a valid
 * index into a non-empty `src` (`0 <= offset <= src.length - 1`; `0` for empty
 * input), a non-empty `expected` description, and the offending token in
 * `found` (Requirement 5.2).
 *
 * Nesting is bounded by `MAX_PARSE_DEPTH` so that hostile input cannot overflow
 * the JavaScript stack. The limit is far above the 32 levels Requirement 4.4
 * asks for; exceeding it is reported as an ordinary parse error.
 */

import { printSheetName } from '../a1.js';

import {
  BINARY_PRECEDENCE,
  isBinOp,
  isRightAssociative,
  type Ast,
  type BinOp,
  type RefNode,
} from './ast.js';
import { describeToken, tokenize, type Token } from './lexer.js';

export interface ParseOk {
  ok: true;
  ast: Ast;
}

export interface ParseErr {
  ok: false;
  offset: number;
  expected: string;
  found: string;
}

export type ParseResult = ParseOk | ParseErr;

/** Maximum syntactic nesting depth accepted (Requirement 4.4 needs >= 32). */
export const MAX_PARSE_DEPTH = 512;

const PRIMARY_EXPECTED = 'a number, text, boolean, reference, function call, or "("';

class ParseFailure extends Error {
  constructor(
    readonly offset: number,
    readonly expected: string,
    readonly found: string,
  ) {
    super(expected);
    this.name = 'ParseFailure';
  }
}

export function parseFormula(src: string): ParseResult {
  const lexed = tokenize(src);
  if (!lexed.ok) {
    return fail(src, lexed.error.offset, lexed.error.expected, lexed.error.found);
  }
  const parser = new Parser(lexed.tokens);
  try {
    return { ok: true, ast: parser.parseTop() };
  } catch (e: unknown) {
    if (e instanceof ParseFailure) return fail(src, e.offset, e.expected, e.found);
    throw e;
  }
}

/** Clamps the offset so it always indexes into the source string. */
function fail(src: string, offset: number, expected: string, found: string): ParseErr {
  const limit = Math.max(0, src.length - 1);
  const clamped = Math.min(Math.max(0, offset), limit);
  return { ok: false, offset: clamped, expected, found: found.length > 0 ? found : 'end of formula' };
}

class Parser {
  private pos = 0;

  constructor(private readonly tokens: readonly Token[]) {}

  parseTop(): Ast {
    const first = this.peek();
    if (first.kind === 'op' && first.op === '=') this.pos += 1;
    const ast = this.parseExpr(1, 0);
    const trailing = this.peek();
    if (trailing.kind !== 'eof') {
      this.raise(trailing, 'an operator or the end of the formula');
    }
    return ast;
  }

  /** Precedence climbing over the binary operators. */
  private parseExpr(minPrec: number, depth: number): Ast {
    this.checkDepth(depth);
    let left = this.parseUnary(depth);
    for (;;) {
      const token = this.peek();
      if (token.kind !== 'op' || !isBinOp(token.op)) break;
      const op: BinOp = token.op;
      const prec = BINARY_PRECEDENCE[op];
      if (prec < minPrec) break;
      this.pos += 1;
      const nextMin = isRightAssociative(op) ? prec : prec + 1;
      const right = this.parseExpr(nextMin, depth + 1);
      left = { k: 'binary', op, left, right };
    }
    return left;
  }

  /** `unary := ('+'|'-')* postfix` */
  private parseUnary(depth: number): Ast {
    this.checkDepth(depth);
    const token = this.peek();
    if (token.kind === 'op' && (token.op === '+' || token.op === '-')) {
      this.pos += 1;
      return { k: 'unary', op: token.op, arg: this.parseUnary(depth + 1) };
    }
    return this.parsePostfix(depth);
  }

  /** `postfix := primary ('%')*` */
  private parsePostfix(depth: number): Ast {
    let arg = this.parsePrimary(depth);
    for (;;) {
      const token = this.peek();
      if (token.kind !== 'op' || token.op !== '%') break;
      this.pos += 1;
      arg = { k: 'unary', op: '%', arg };
    }
    return arg;
  }

  private parsePrimary(depth: number): Ast {
    this.checkDepth(depth);
    const token = this.peek();
    switch (token.kind) {
      case 'num':
        this.pos += 1;
        return { k: 'num', v: token.value };
      case 'str':
        this.pos += 1;
        return { k: 'str', v: token.value };
      case 'bool':
        this.pos += 1;
        return { k: 'bool', v: token.value };
      case 'err':
        this.pos += 1;
        return { k: 'err', v: token.value };
      case 'ref':
        this.pos += 1;
        return this.parseRefOrRange(token.ref);
      case 'name':
        this.pos += 1;
        return this.parseNameOrCall(token.name, depth);
      case 'lparen': {
        this.pos += 1;
        const inner = this.parseExpr(1, depth + 1);
        this.expect('rparen', '")"');
        return inner;
      }
      default:
        return this.raise(token, PRIMARY_EXPECTED);
    }
  }

  /** `ref [':' ref]`, keeping any sheet qualifier on the left-hand reference. */
  private parseRefOrRange(from: RefNode): Ast {
    if (this.peek().kind !== 'colon') return from;
    this.pos += 1;
    const token = this.peek();
    if (token.kind !== 'ref') return this.raise(token, 'a cell reference after ":"');
    this.pos += 1;
    const right = token.ref;
    if (right.sheet !== undefined && right.sheet !== from.sheet) {
      return this.raise(
        token,
        from.sheet === undefined
          ? 'a cell reference without a sheet name'
          : // The sheet name comes from the input, so it is printed through the
            // canonical quoting rule. A bare name cannot contain whitespace, and
            // a quoted one is delimited, so the description never picks up stray
            // leading or trailing whitespace from a name like `'Q1 '`.
            `a cell reference on sheet ${printSheetName(from.sheet)}`,
      );
    }
    const to: RefNode = {
      k: 'ref',
      absCol: right.absCol,
      col: right.col,
      absRow: right.absRow,
      row: right.row,
    };
    return { k: 'range', from, to };
  }

  private parseNameOrCall(name: string, depth: number): Ast {
    if (this.peek().kind !== 'lparen') return { k: 'name', name };
    this.pos += 1;
    const args: Ast[] = [];
    if (this.peek().kind !== 'rparen') {
      for (;;) {
        args.push(this.parseExpr(1, depth + 1));
        if (this.peek().kind !== 'comma') break;
        this.pos += 1;
      }
    }
    this.expect('rparen', '"," or ")"');
    return { k: 'call', name: name.toUpperCase(), args };
  }

  private expect(kind: Token['kind'], expected: string): Token {
    const token = this.peek();
    if (token.kind !== kind) this.raise(token, expected);
    this.pos += 1;
    return token;
  }

  private checkDepth(depth: number): void {
    if (depth > MAX_PARSE_DEPTH) {
      this.raise(this.peek(), `a formula nested no deeper than ${MAX_PARSE_DEPTH} levels`);
    }
  }

  private peek(): Token {
    return this.tokens[Math.min(this.pos, this.tokens.length - 1)] as Token;
  }

  private raise(token: Token, expected: string): never {
    throw new ParseFailure(token.offset, expected, describeToken(token));
  }
}
